import React from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { map } from 'lodash-es';

import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuHeader,
  MenuItem,
  useMenuProps,
} from '../../../components/DropdownSet';

const MetricList = ({
  selected,
  options,
  disabled,
  menuWidth = 220,
  appendButtonText,
  className,
  onChange,
}) => {
  const menuProps = useMenuProps({
    align: 'start',
    menuStyle: { width: menuWidth },
    className,
  });

  const decoratedOptions = appendButtonText
    ? options.map((option) => ({
        ...option,
        buttonLabel: (
          <>
            <span>{option.label}</span>
            <span className="label-caption">{appendButtonText}</span>
          </>
        ),
      }))
    : options;

  const selectedOption = decoratedOptions.find((option) => option.value === selected) || {};

  const handleChange = ({ value }) => {
    onChange(value);
  };

  return (
    <div className={`metric-list-dropdown ${className || ''}`}>
      <Menu
        menuButton={
          <MenuButton disabled={disabled} frameless caret variant="transparent">
            {selectedOption.buttonLabel || selectedOption.label}
          </MenuButton>
        }
        {...menuProps}
      >
        {options.map(({ label, value, className: optionClassName }, index) => {
          if (value === 'divider') return <MenuDivider key={`divider-${index}`} fullWidth dense />;
          if (value === 'header') return <MenuHeader key={`header-${index}`}>{label}</MenuHeader>;

          const isActive = selectedOption.value === value;

          return (
            <MenuItem
              key={value}
              value={value}
              className={cx({ active: isActive }, optionClassName)}
              data-testid={value}
              onClick={(event) => !isActive && handleChange(event)}
            >
              {label}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

const basePropTypes = {
  selected: T.string.isRequired,
  disabled: T.bool,
  onChange: T.func.isRequired,
};

MetricList.propTypes = {
  ...basePropTypes,
  appendButtonText: T.string,
  options: T.arrayOf(
    T.shape({
      label: T.string,
      value: T.string,
      className: T.string,
    })
  ).isRequired,
};

const metricsToOptions = (metrics) =>
  map(metrics, (label, value) => ({
    label,
    value,
    className: 'menu-item--14',
  }));

// ActivityMetricDropdown
export const activityMetrics = {
  dau: 'DAU',
  downloads: 'Downloads',
};

const activityMetricsOptions = metricsToOptions(activityMetrics);
export const ActivityMetricDropdown = ({ selected, disabled, onChange }) => (
  <MetricList
    selected={selected}
    disabled={disabled}
    options={activityMetricsOptions}
    menuWidth={118}
    onChange={onChange}
  />
);

ActivityMetricDropdown.propTypes = basePropTypes;

const dauMarketShareMetrics = {
  dau: 'Market Share',
  dau_cumulative_change: 'Cumulative Share',
  dau_pop_change: 'Change in Market Share (PoP)',
};

const downloadsMarketShareMetrics = {
  downloads: 'Market Share',
  downloads_cumulative_change: 'Cumulative Share',
  downloads_pop_change: 'Change in Market Share (PoP)',
};

export const marketShareMetrics = {
  ...dauMarketShareMetrics,
  ...downloadsMarketShareMetrics,
};

const marketShareMetricsOptions = [
  { label: 'DAU', value: 'header' },
  ...metricsToOptions(dauMarketShareMetrics).map((option) => ({
    ...option,
    buttonLabel: `${option.label} - DAU`,
  })),
  { value: 'divider' },
  { label: 'Downloads', value: 'header' },
  ...metricsToOptions(downloadsMarketShareMetrics).map((option) => ({
    ...option,
    buttonLabel: `${option.label} - Downloads`,
  })),
];

export const MarketShareDropdown = ({ selected, disabled, onChange }) => (
  <MetricList
    selected={selected}
    disabled={disabled}
    options={marketShareMetricsOptions}
    menuWidth={238}
    onChange={onChange}
  />
);

MarketShareDropdown.propTypes = basePropTypes;

export const segmentConcentrationMetrics = {
  high_frequency_ai_users: 'Segment Concentration — High-Frequency Users',
  medium_frequency_ai_users: 'Segment Concentration — Medium-Frequency Users',
};

const segmentConcentrationMetricsOptions = metricsToOptions(segmentConcentrationMetrics);
export const SegmentConcentrationDropdown = ({ selected, disabled, onChange }) => (
  <MetricList
    selected={selected}
    disabled={disabled}
    options={segmentConcentrationMetricsOptions}
    menuWidth={385}
    className="header-dropdown"
    onChange={onChange}
  />
);

SegmentConcentrationDropdown.propTypes = basePropTypes;

export const timeSpentMetrics = {
  high_frequency_ai_users: 'Time Spent — High-Frequency AI Users',
  medium_frequency_ai_users: 'Time Spent — Medium-Frequency AI Users',
};

const timeSpentMetricsOptions = metricsToOptions(timeSpentMetrics);
export const TimeSpentDropdown = ({ selected, disabled, onChange }) => (
  <MetricList
    selected={selected}
    disabled={disabled}
    options={timeSpentMetricsOptions}
    menuWidth={345}
    onChange={onChange}
  />
);

TimeSpentDropdown.propTypes = basePropTypes;

export const appImpactMetrics = {
  average_time_spent_per_dau: 'Avg Time Spent per DAU',
  average_sessions_per_dau: 'Average sessions per DAU',
};

const appImpactMetricsOptions = metricsToOptions(appImpactMetrics);
export const AppImpactDropdown = ({ selected, disabled, onChange }) => (
  <MetricList
    selected={selected}
    disabled={disabled}
    options={appImpactMetricsOptions}
    menuWidth={220}
    className="header-dropdown"
    onChange={onChange}
  />
);

AppImpactDropdown.propTypes = basePropTypes;
